import { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { TopicFilterProps, PublicTopic } from '@/types/blog';

const TopicFilter = ({ 
  topics, 
  selectedTopicId, 
  onTopicChange, 
  loading = false,
  className = ''
}: TopicFilterProps & { className?: string }) => {
  // Find selected topic or create "All topics" option
  const allTopicsOption: PublicTopic = {
    id: 0,
    title: 'Tất cả chủ đề',
    description: 'Hiển thị tất cả bài viết',
    slug: 'all',
    articleCount: topics.reduce((sum, topic) => sum + topic.articleCount, 0)
  };

  const selectedTopic = selectedTopicId 
    ? topics.find(topic => topic.id === selectedTopicId) || allTopicsOption
    : allTopicsOption;

  // All options including "All topics"
  const allOptions = [allTopicsOption, ...topics];

  const handleChange = (topic: PublicTopic) => {
    // Immediately trigger the change to provide instant feedback
    if (topic.id === 0) {
      onTopicChange(undefined);
    } else {
      onTopicChange(topic.id);
    }
  };

  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <div className="animate-pulse bg-m2-surface-highest rounded-eh h-12 w-48"></div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <Listbox value={selectedTopic} onChange={handleChange}>
        <div className="relative">
          <Listbox.Button className="relative w-full cursor-default rounded-eh bg-m2-surface-lowest py-3 pl-3 pr-10 text-left border border-eh-border focus:outline-none focus:ring-2 focus:ring-eh-forest-ink/30 focus:border-eh-forest-ink hover:border-eh-forest-ink/50 transition-colors">
            <span className="flex items-center">
              <span className="block truncate font-medium text-m2-on-surface">
                {selectedTopic.title}
              </span>
              <span className="ml-2 text-sm text-m2-on-surface-variant">
                ({selectedTopic.articleCount})
              </span>
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-m2-outline"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-eh bg-m2-surface-lowest py-1 text-base shadow-eh-soft ring-1 ring-eh-border focus:outline-none sm:text-sm">
              {allOptions.map((topic) => (
                <Listbox.Option
                  key={topic.id}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                      active ? 'bg-m2-primary/15 text-m2-on-surface' : 'text-m2-on-surface'
                    }`
                  }
                  value={topic}
                >
                  {({ selected, active }) => (
                    <>
                      <div className="flex items-center">
                        <span
                          className={`block truncate ${
                            selected ? 'font-medium' : 'font-normal'
                          }`}
                        >
                          {topic.title}
                        </span>
                        <span 
                          className={`ml-2 text-sm ${
                            active ? 'text-m2-primary' : 'text-m2-on-surface-variant'
                          }`}
                        >
                          ({topic.articleCount})
                        </span>
                      </div>
                      {topic.description && topic.id !== 0 && (
                        <p className={`mt-1 text-xs ${
                          active ? 'text-m2-primary' : 'text-m2-on-surface-variant'
                        } truncate pl-0`}>
                          {topic.description}
                        </p>
                      )}
                      {selected ? (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                            active ? 'text-m2-primary' : 'text-m2-primary'
                          }`}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};

export default TopicFilter;